import { useState } from 'react';
import type { CandidateAPIV2 } from '../types';

interface Props {
  candidate: CandidateAPIV2;
}

const TOP_SKILLS_VISIBLE = 5;

const AVATAR_PALETTE = [
  'bg-brand-100 text-brand-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-blue-100 text-blue-700',
  'bg-pink-100 text-pink-700',
];

/** Two-letter initials from a person's name, e.g. "Hossam Alkafri" → "HA". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic palette pick from a string — same name always gets the same color. */
function avatarPalette(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export default function CandidateCard({ candidate }: Props) {
  const [expanded, setExpanded] = useState(false);

  const matched = candidate.skills?.matched ?? [];
  const visibleSkills = expanded ? matched : matched.slice(0, TOP_SKILLS_VISIBLE);
  const hiddenCount = Math.max(0, matched.length - TOP_SKILLS_VISIBLE);
  const percent = Math.round((candidate.skills?.score ?? 0) * 100);

  return (
    <article className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start gap-4">
        {/* Avatar — photo if available, otherwise coloured initials */}
        {candidate.photo ? (
          <img
            src={candidate.photo}
            alt=""
            aria-hidden="true"
            className="h-12 w-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div
            aria-hidden="true"
            className={`h-12 w-12 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${avatarPalette(candidate.name)}`}
          >
            {initials(candidate.name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Name + match badge */}
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {candidate.name}
            </h3>
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-green-50 text-green-700 border-green-200 shrink-0">
              {percent}% match
            </span>
          </div>

          <p className="text-xs text-gray-500 mt-2 italic">
            Not yet analyzed · skill-match signal only
          </p>

          {/* Matched skill chips */}
          {matched.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {visibleSkills.map((skill) => (
                <li key={skill}>
                  <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                    {skill}
                  </span>
                </li>
              ))}
              {!expanded && hiddenCount > 0 && (
                <li>
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="inline-block bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-xs px-2 py-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    aria-label={`Show ${hiddenCount} more matched skills`}
                  >
                    +{hiddenCount} more
                  </button>
                </li>
              )}
              {expanded && matched.length > TOP_SKILLS_VISIBLE && (
                <li>
                  <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="inline-block bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-xs px-2 py-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    aria-label="Collapse skill list"
                  >
                    Show less
                  </button>
                </li>
              )}
            </ul>
          )}

          {/* Disabled "Run analysis" placeholder — wire the action when the
              analysis backend lands. */}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              disabled
              className="flex-1 inline-flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 text-sm font-medium px-3 py-2 gap-1.5 cursor-not-allowed"
              aria-label="Run analysis (not yet available)"
            >
              <span aria-hidden="true">▶</span>
              <span>Run analysis</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
